import React from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import Text from '../Text';
import { Ionicons } from '../Icons';
import { useStyles } from '~/hooks';
import { FONT_SIZE_L } from '~/constants/typography';
import { isDesktop, scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    visible: boolean;
    onClose: () => void;
    // Translated via the core Text component (i18n key or plain text).
    title?: string;
    // Set when the title is already human-readable and has no i18n key — e.g. an indicator's
    // display name coming from the backend templates.
    titleNoTranslate?: boolean;
    children: React.ReactNode;
};

/**
 * The app's action-sheet / options surface. On phones and narrow web it presents as a
 * bottom sheet (slide-up over a dimmed backdrop); above the desktop breakpoint it presents
 * the same children as a centered dialog, so call sites never branch on platform or width.
 * Implemented on plain Modal (no gesture-handler root requirements); the internals can be
 * swapped for a gesture-driven sheet later without touching the API.
 */
export default function AppBottomSheet({ visible, onClose, title, titleNoTranslate, children }: Props) {
    const styles = useStyles(stylesGenerator);
    const desktop = isDesktop();

    return (
        <Modal visible={visible} transparent animationType={desktop ? 'fade' : 'slide'} onRequestClose={onClose}>
            <View style={[styles.backdropContainer, desktop ? styles.backdropContainerDesktop : null]}>
                <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel="Close" />
                <View style={[styles.sheet, desktop ? styles.sheetDesktop : null]}>
                    <View style={styles.headerRow}>
                        {title ? (
                            <Text noTranslate={titleNoTranslate} fontType="LATO_BOLD" style={styles.title}>
                                {title}
                            </Text>
                        ) : (
                            <View />
                        )}
                        <Pressable hitSlop={8} onPress={onClose} accessibilityLabel="Close">
                            <Ionicons name="close" size={scaleSize(22)} style={styles.closeIcon} />
                        </Pressable>
                    </View>
                    <ScrollView
                        style={styles.content}
                        contentContainerStyle={styles.contentContainer}
                        keyboardShouldPersistTaps="handled"
                    >
                        {children}
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
}

type SheetActionProps = {
    label: string;
    onPress: () => void;
    icon?: React.ComponentProps<typeof Ionicons>['name'];
    destructive?: boolean;
    disabled?: boolean;
};

// A single tappable row inside an AppBottomSheet menu.
export function SheetAction({ label, onPress, icon, destructive, disabled }: SheetActionProps) {
    const styles = useStyles(stylesGenerator);
    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            disabled={disabled}
            style={({ pressed }) => [styles.actionRow, (pressed || disabled) && styles.actionRowDimmed]}
        >
            {icon ? (
                <Ionicons
                    name={icon}
                    size={scaleSize(20)}
                    style={[styles.actionIcon, destructive ? styles.destructive : null]}
                />
            ) : null}
            <Text style={StyleSheet.flatten([styles.actionLabel, destructive ? styles.destructive : null])}>
                {label}
            </Text>
        </Pressable>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        backdropContainer: {
            flex: 1,
            justifyContent: 'flex-end',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        backdropContainerDesktop: {
            justifyContent: 'center',
            alignItems: 'center',
        },
        sheet: {
            backgroundColor: theme.background,
            borderTopLeftRadius: scaleSize(16),
            borderTopRightRadius: scaleSize(16),
            paddingBottom: scaleSize(24),
            maxHeight: '85%',
        },
        sheetDesktop: {
            width: 480,
            maxWidth: '90%',
            maxHeight: '80%',
            borderRadius: scaleSize(12),
        },
        headerRow: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: scaleSize(16),
            paddingTop: scaleSize(14),
            paddingBottom: scaleSize(6),
        },
        title: {
            fontSize: FONT_SIZE_L,
            color: theme.text,
        },
        closeIcon: {
            color: theme.textSecondary,
        },
        content: {
            flexGrow: 0,
        },
        contentContainer: {
            paddingHorizontal: scaleSize(16),
            paddingTop: scaleSize(4),
        },
        actionRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: scaleSize(13),
        },
        actionRowDimmed: {
            opacity: 0.5,
        },
        actionIcon: {
            color: theme.text,
            marginRight: scaleSize(12),
        },
        actionLabel: {
            fontSize: FONT_SIZE_L,
            color: theme.text,
        },
        destructive: {
            color: theme.alert,
        },
    });
